import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { colorMemoryFaqs } from '../components/FAQSection';
import {
  COLOR_MEMORY_POINT_THRESHOLD,
  colorDistance,
  getColorMemoryRank,
  hslToHex,
  hslToRgb,
  randomColor,
  scoreFromDistance,
  scoresColorMemoryPoint,
} from '../utils/colorUtils';
import { getItem, setItem } from '../utils/safeStorage';
import '../styles/ColorMemory.css';

const HIGHSCORE_KEY = 'cm-color-memory-streak';
const SHOW_SECONDS = 5;
const HIT_CONTINUE_MS = 1400;

export default function ColorMemoryPage() {
  const [phase, setPhase] = useState('idle');
  const [target, setTarget] = useState(() => randomColor());
  const [guess, setGuess] = useState({ h: 0, s: 50, l: 50 });
  const [countdown, setCountdown] = useState(SHOW_SECONDS);
  const [score, setScore] = useState(null);
  const [distance, setDistance] = useState(null);
  const [points, setPoints] = useState(0);
  const [highscore, setHighscore] = useState(0);
  const timerRef = useRef(null);
  const hitTimeoutRef = useRef(null);
  const beginShowRef = useRef(() => {});

  useEffect(() => {
    const stored = Number(getItem(HIGHSCORE_KEY));
    if (Number.isFinite(stored) && stored > 0) setHighscore(stored);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (hitTimeoutRef.current) clearTimeout(hitTimeoutRef.current);
  }, []);

  const saveHighscore = useCallback((nextPoints) => {
    setHighscore((best) => {
      if (nextPoints <= best) return best;
      setItem(HIGHSCORE_KEY, String(nextPoints));
      return nextPoints;
    });
  }, []);

  const beginShow = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTarget(randomColor());
    setGuess({ h: 0, s: 50, l: 50 });
    setCountdown(SHOW_SECONDS);
    setPhase('showing');

    let remaining = SHOW_SECONDS;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setPhase('picking');
      }
    }, 1000);
  }, []);

  beginShowRef.current = beginShow;

  const startGame = useCallback(() => {
    if (hitTimeoutRef.current) {
      clearTimeout(hitTimeoutRef.current);
      hitTimeoutRef.current = null;
    }
    setPoints(0);
    setScore(null);
    setDistance(null);
    beginShow();
  }, [beginShow]);

  const submitGuess = useCallback(() => {
    if (phase !== 'picking') return;

    const targetRgb = hslToRgb(target.h, target.s, target.l);
    const guessRgb = hslToRgb(guess.h, guess.s, guess.l);
    const dist = colorDistance(targetRgb, guessRgb);
    const nextScore = scoreFromDistance(dist);

    setDistance(dist);
    setScore(nextScore);

    if (scoresColorMemoryPoint(nextScore)) {
      setPoints((prev) => {
        const nextPoints = prev + 1;
        saveHighscore(nextPoints);
        return nextPoints;
      });
      setPhase('hit');
      if (hitTimeoutRef.current) clearTimeout(hitTimeoutRef.current);
      hitTimeoutRef.current = setTimeout(() => {
        hitTimeoutRef.current = null;
        beginShowRef.current();
      }, HIT_CONTINUE_MS);
    } else {
      setPhase('result');
    }
  }, [guess, phase, saveHighscore, target]);

  const targetHex = hslToHex(target.h, target.s, target.l);
  const guessHex = hslToHex(guess.h, guess.s, guess.l);
  const rank = score != null ? getColorMemoryRank(score) : null;
  const inRun = phase !== 'idle';
  const seo = getPageSeo('color-memory');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(colorMemoryFaqs)]
    : buildFAQSchema(colorMemoryFaqs);

  const compareSwatches = (
    <div className="cm-compare">
      <div>
        <div className="cm-swatch" style={{ backgroundColor: targetHex }} />
        <p className="cm-swatch-caption">Target</p>
      </div>
      <span className="cm-compare-arrow" aria-hidden="true">→</span>
      <div>
        <div className="cm-swatch" style={{ backgroundColor: guessHex }} />
        <p className="cm-swatch-caption">Your guess</p>
      </div>
    </div>
  );

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="cm-game" aria-label="Color memory game">
        <h1 className="cm-visually-hidden">Color Memory: Guess the Color</h1>

        <header className="cm-header">
          <div className="cm-score-block">
            <span className="cm-score-number">{highscore}</span>
            <span className="cm-score-label">Best Streak</span>
          </div>
          {inRun && (
            <div className="cm-score-block">
              <span className="cm-score-number">{points}</span>
              <span className="cm-score-label">Points</span>
            </div>
          )}
          {score != null && (
            <div className="cm-score-block">
              <span className="cm-score-number">{score}/100</span>
              <span className="cm-score-label">Accuracy</span>
            </div>
          )}
        </header>

        <div className="cm-stage">
          {phase === 'idle' && (
            <div className="cm-idle-card">
              <h2>Color Memory</h2>
              <p>
                A color appears for {SHOW_SECONDS} seconds. Memorize it, then
                recreate it with the sliders. Score {COLOR_MEMORY_POINT_THRESHOLD} or
                higher to earn a point and get a new color. Miss, and the run is over.
              </p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={startGame}>
                Start Game
              </button>
            </div>
          )}

          {phase === 'showing' && (
            <div
              className="cm-show-panel"
              style={{ backgroundColor: targetHex }}
              data-cm-h={target.h}
              data-cm-s={target.s}
              data-cm-l={target.l}
              aria-label="Memorize this color"
            >
              <span className="cm-show-label">Memorize</span>
              <span className="cm-countdown" aria-live="polite">{countdown}</span>
            </div>
          )}

          {phase === 'picking' && (
            <div className="cm-picker-card">
              <h2>Recreate the Color</h2>
              <p>
                Adjust hue, saturation, and lightness to match what you remember.
                You need {COLOR_MEMORY_POINT_THRESHOLD}+ to score a point.
              </p>
              <div
                className="cm-preview"
                style={{ backgroundColor: guessHex }}
                aria-hidden="true"
              />
              <div className="cm-slider-group">
                <label htmlFor="cm-hue">
                  <span>Hue</span>
                  <span>{guess.h}°</span>
                </label>
                <input
                  id="cm-hue"
                  type="range"
                  min="0"
                  max="359"
                  value={guess.h}
                  onChange={(event) => setGuess((prev) => ({ ...prev, h: Number(event.target.value) }))}
                />
              </div>
              <div className="cm-slider-group">
                <label htmlFor="cm-sat">
                  <span>Saturation</span>
                  <span>{guess.s}%</span>
                </label>
                <input
                  id="cm-sat"
                  type="range"
                  min="0"
                  max="100"
                  value={guess.s}
                  onChange={(event) => setGuess((prev) => ({ ...prev, s: Number(event.target.value) }))}
                />
              </div>
              <div className="cm-slider-group">
                <label htmlFor="cm-light">
                  <span>Lightness</span>
                  <span>{guess.l}%</span>
                </label>
                <input
                  id="cm-light"
                  type="range"
                  min="0"
                  max="100"
                  value={guess.l}
                  onChange={(event) => setGuess((prev) => ({ ...prev, l: Number(event.target.value) }))}
                />
              </div>
              <button type="button" className="cm-btn cm-btn-primary" onClick={submitGuess}>
                Submit Guess
              </button>
            </div>
          )}

          {phase === 'hit' && score != null && rank && (
            <div className="cm-result-card" aria-live="polite">
              <h2>Point scored</h2>
              <p className="cm-run-points">
                {points} {points === 1 ? 'point' : 'points'}
              </p>
              <p className="cm-result-score">{score}/100</p>
              <div className="cm-rank-row">
                <span className="cm-rank-label">Your Rank:</span>
                <span className="cm-rank-animal">{rank.animal}</span>
              </div>
              <p className="cm-result-label">{rank.message}</p>
              {compareSwatches}
              <p className="cm-result-meta">
                {COLOR_MEMORY_POINT_THRESHOLD}+ accuracy — next color incoming.
              </p>
            </div>
          )}

          {phase === 'result' && score != null && rank && (
            <div className="cm-result-card">
              <h2>Game Over</h2>
              <p className="cm-run-points">
                {points} {points === 1 ? 'point' : 'points'}
              </p>
              <p className="cm-result-score">{score}/100</p>
              <p className="cm-miss-note">
                Needed {COLOR_MEMORY_POINT_THRESHOLD} or higher. No point for that color.
              </p>
              <div className="cm-rank-row">
                <span className="cm-rank-label">Your Rank:</span>
                <span className="cm-rank-animal">{rank.animal}</span>
              </div>
              <p className="cm-result-label">{rank.message}</p>
              {compareSwatches}
              <p className="cm-result-meta">
                Color distance: {Math.round(distance)} (lower is closer) · Best streak: {highscore}
              </p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={startGame}>
                Play Again
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="cm-extras">
        <h2>How to Play Color Memory</h2>
        <p>
          Study the color while the countdown runs. When it disappears, rebuild
          it with the HSL sliders and submit. Your accuracy is 0–100 based on
          how close your RGB values are to the original. Only {COLOR_MEMORY_POINT_THRESHOLD}+
          scores a point and another color. Anything lower ends the run.
        </p>
        <ul>
          <li>You have {SHOW_SECONDS} seconds to memorize each color.</li>
          <li>There is no time limit while picking — focus on accuracy.</li>
          <li>
            Score {COLOR_MEMORY_POINT_THRESHOLD} or higher to earn 1 point and continue.
            Below {COLOR_MEMORY_POINT_THRESHOLD} is a miss — game over.
          </li>
          <li>Your best streak is saved in this browser.</li>
        </ul>
      </section>

      <FAQSection mode="colorMemory" />
    </>
  );
}
